// Offline-first local store for Mango Notes & labels.
// Writes to localStorage immediately; mirrors to backend when online AND
// the browser is signed in. When the user is a guest (no auth), all CRUD
// stays strictly local — backend is never called for notes/labels so a
// fresh visitor on a different device sees a blank app.

import axios from "axios";
import { merge3 } from "@/lib/merge3";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, timeout: 7000, withCredentials: true });

const K_NOTES = "keepflow.notes";
const K_LABELS = "keepflow.labels";
const K_QUEUE = "keepflow.pendingOps";
const K_LAST_SYNC = "keepflow.lastSync";

// Auth gate — flipped by AuthContext. `"user"` = sync to backend;
// `"guest"` or `null` = local-only (never call /notes or /labels endpoints).
let _authMode = null;
export function setAuthMode(mode) { _authMode = mode; }
const isSignedIn = () => _authMode === "user";

const nowIso = () => new Date().toISOString();
const genId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : "n_" + Math.random().toString(36).slice(2) + Date.now().toString(36);

const read = (k, def) => {
  try { return JSON.parse(localStorage.getItem(k) || "null") ?? def; }
  catch { return def; }
};
const write = (k, v) => localStorage.setItem(k, JSON.stringify(v));

const subs = new Set();
export const subscribe = (fn) => { subs.add(fn); return () => subs.delete(fn); };
export const emitChange = () => emit();
const emit = () => subs.forEach((f) => {
  try { f(); } catch (err) {
    // A subscriber crashed — don't let it break the broadcast to others.
    console.warn("[offlineStore] subscriber threw:", err?.message || err);
  }
});

export const isOnline = () => (typeof navigator !== "undefined" ? navigator.onLine : true);

// ---------------- Pending queue ----------------
const getQueue = () => read(K_QUEUE, []);
const setQueue = (q) => write(K_QUEUE, q);
const enqueue = (op) => setQueue([...getQueue(), { ...op, enqueuedAt: nowIso() }]);

async function tryRemote(fn) {
  if (!isOnline()) throw new Error("offline");
  try { return await fn(); }
  catch (e) {
    // treat network errors as offline
    if (!e?.response) throw new Error("offline");
    throw e;
  }
}

// ---------------- Notes ----------------
export function filterNotes(view, activeLabel, q) {
  const all = read(K_NOTES, []);
  const hasQuery = !!(q && q.trim());
  const matchView = (n) => {
    if (view === "archive") return n.archived && !n.trashed;
    if (view === "trash") return !!n.trashed;
    if (view === "reminders") return !n.trashed && !n.archived && !!n.reminder;
    // When the user is actively searching we broaden the Notes view to
    // span every live container — labelled notes AND archived notes — so
    // Mango Notes behaves like Google Keep's cross-corpus search. Only
    // trashed items stay excluded.
    if (hasQuery) return !n.trashed;
    // Main "Notes" view: show every active note. Labels are an ADDITIONAL
    // filter the user applies by clicking the label in the sidebar (which
    // sets activeLabel) — not a hiding rule. Earlier iterations hid any
    // labelled note from the main view, which confused users who added a
    // label and then couldn't find their note.
    return !n.archived && !n.trashed;
  };
  const matchLabel = (n) => (activeLabel ? (n.labels || []).includes(activeLabel) : true);
  const matchQuery = (n) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    if ((n.title || "").toLowerCase().includes(needle)) return true;
    if ((n.content || "").toLowerCase().includes(needle)) return true;
    if ((n.labels || []).some((l) => l.toLowerCase().includes(needle))) return true;
    if ((n.checklist_items || []).some((i) => (i.text || "").toLowerCase().includes(needle))) return true;
    return false;
  };
  const sorted = all
    .filter(matchView)
    .filter(matchLabel)
    .filter(matchQuery)
    .sort((a, b) => {
      if (!!b.pinned - !!a.pinned !== 0) return !!b.pinned - !!a.pinned;
      return (b.updated_at || "").localeCompare(a.updated_at || "");
    });

  // Reminders view: resort so TODAY floats to the top, then the rest of
  // the upcoming reminders in ascending date order, finally past ones at
  // the bottom. The pinned-first rule still wins at the very top.
  if (view === "reminders") {
    const now = new Date();
    const sod = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const eod = sod + 86_400_000;
    const bucket = (n) => {
      const t = new Date(n.reminder || 0).getTime();
      if (t >= sod && t < eod) return 0;       // today
      if (t >= eod) return 1;                   // upcoming
      return 2;                                 // past
    };
    sorted.sort((a, b) => {
      // keep pinned always on top
      if (!!b.pinned - !!a.pinned !== 0) return !!b.pinned - !!a.pinned;
      const ba = bucket(a), bb = bucket(b);
      if (ba !== bb) return ba - bb;
      // within each bucket, soonest first (upcoming) OR most-recent first (past)
      const ta = new Date(a.reminder || 0).getTime();
      const tb = new Date(b.reminder || 0).getTime();
      return ba === 2 ? tb - ta : ta - tb;
    });
  }

  return sorted;
}

export async function listNotes(params = {}) {
  // Guest mode: never talk to backend. Local cache is the source of truth.
  if (!isSignedIn()) {
    return filterNotes(params.view || "notes", params.label, params.q);
  }
  // Try remote first when online; always return from local cache.
  if (isOnline()) {
    try {
      const { data } = await client.get("/notes", { params: { view: params.view || "notes" } });
      // Keep local cache in sync (merge remote by id, preserve local-only pending).
      const local = read(K_NOTES, []);
      const localPendingIds = new Set(local.filter((n) => n._pending).map((n) => n.id));
      // Build a full superset: remote notes + local-only pending ones
      const remoteById = new Map(data.map((n) => [n.id, n]));
      const existing = new Map(local.map((n) => [n.id, n]));
      const merged = [];
      // All remote notes override local (unless local has pending ops not yet synced)
      for (const n of data) {
        const l = existing.get(n.id);
        if (l && l._pending) merged.push(l); else merged.push(n);
      }
      // Add local-only pending notes not in remote
      for (const l of local) {
        if (!remoteById.has(l.id) && (l._pending || l._localOnly)) merged.push(l);
      }
      write(K_NOTES, merged);
      write(K_LAST_SYNC, nowIso());
      emit();
    } catch (e) { /* fall back to local */ }
  }
  return filterNotes(params.view || "notes", params.label, params.q);
}

export async function createNote(payload) {
  const note = {
    id: genId(),
    title: payload.title || "",
    content: payload.content || "",
    is_checklist: !!payload.is_checklist,
    checklist_items: payload.checklist_items || [],
    color: payload.color || "default",
    labels: payload.labels || [],
    pinned: !!payload.pinned,
    archived: false,
    trashed: false,
    reminder: payload.reminder || null,
    reminder_recurrence: payload.reminder_recurrence || "none",
    images: payload.images || [],
    attachments: payload.attachments || [],
    created_at: nowIso(),
    updated_at: nowIso(),
    _localOnly: true,
  };
  const notes = [note, ...read(K_NOTES, [])];
  write(K_NOTES, notes);
  emit();

  // Guest mode: stay 100% local. The note already lives in localStorage,
  // nothing to enqueue because there's no account to sync to.
  if (!isSignedIn()) return note;

  try {
    const { data } = await tryRemote(() => client.post("/notes", payload));
    // Backend assigned an id — replace the temp one.
    const next = read(K_NOTES, []).map((n) =>
      n.id === note.id ? { ...data, _localOnly: false } : n
    );
    write(K_NOTES, next);
    emit();
    return data;
  } catch {
    enqueue({ type: "create", localId: note.id, payload });
    return note;
  }
}

export async function updateNote(id, patch, { onConflict } = {}) {
  const notes = read(K_NOTES, []);
  const idx = notes.findIndex((n) => n.id === id);
  const previous = idx >= 0 ? notes[idx] : null;
  // `_base` = the note as the editor first saw it; lets the server merge in a
  // collaborator's changes made meanwhile instead of overwriting them.
  const { _base, ...cleanPatch } = patch;

  // Safety: don't let the client overwrite content/checklist of a locked note
  // with the empty redacted values it got from GET /notes (that happens when
  // the editor is closed without first unlocking).
  const safePatch = { ...cleanPatch };
  if (previous?.locked) {
    if ("content" in safePatch && !safePatch.content) delete safePatch.content;
    if ("checklist_items" in safePatch && (!safePatch.checklist_items || safePatch.checklist_items.length === 0)) {
      delete safePatch.checklist_items;
    }
  }

  if (idx >= 0) {
    // Snapshot BEFORE applying the patch so "Version history" lets the user
    // step back to each prior committed state.
    try { snapshotVersion(id, notes[idx]); } catch (err) {
      console.warn("[offlineStore] snapshotVersion failed:", err?.message || err);
    }
    const localPatch = _base ? merge3(notes[idx], safePatch, _base) : safePatch;
    notes[idx] = { ...notes[idx], ...localPatch, updated_at: nowIso() };
    write(K_NOTES, notes);
    emit();
  }
  if (!isSignedIn()) return notes[idx];
  try {
    const body = { ...safePatch };
    if (previous?.updated_at && !previous._localOnly) body.base_updated_at = previous.updated_at;
    const baseNote = _base && !_base._localOnly ? _base : null;
    if (baseNote?.updated_at && !previous?._localOnly) {
      body.base_updated_at = baseNote.updated_at;
      body.base = Object.fromEntries(Object.keys(safePatch).map((k) => [k, baseNote[k] ?? null]));
    }
    const { data } = await tryRemote(() => client.put(`/notes/${id}`, body));
    const after = read(K_NOTES, []).map((n) => (n.id === id ? data : n));
    write(K_NOTES, after);
    emit();
    return data;
  } catch (e) {
    if (e?.response?.status === 409) {
      const serverCurrent = e.response.data?.detail?.current;
      if (serverCurrent) {
        const after = read(K_NOTES, []).map((n) => (n.id === id ? serverCurrent : n));
        write(K_NOTES, after);
        emit();
        onConflict?.(serverCurrent);
        return serverCurrent;
      }
    }
    enqueue({ type: "update", id, patch: safePatch });
    return notes[idx];
  }
}

export async function duplicateNote(id) {
  const notes = read(K_NOTES, []);
  const orig = notes.find((n) => n.id === id);
  if (!orig) throw new Error("Note not found");
  if (orig.locked) throw new Error("Unlock the note first");
  // Reuse the proven createNote path (which already handles the local-write
  // + server-POST + id-swap flow atomically). Previously we did a local
  // write + enqueued create + fire-and-forget drain, which raced with the
  // next GET /notes and sometimes dropped the copy from the merged list.
  // Spec: only copy title + content (+ checklist state + color).
  return createNote({
    title: (orig.title || "") + " (copy)",
    content: orig.content || "",
    is_checklist: !!orig.is_checklist,
    checklist_items: (orig.checklist_items || []).map((i) => ({ ...i, id: genId() })),
    color: orig.color || "default",
  });
}


// Local-first version history. We snapshot every meaningful change into a
// per-note ring buffer in localStorage (cap: 10 entries) so the "Version
// history" menu item works offline AND for guests. When the user is signed
// in and online we also hydrate the backend's authoritative list.
const K_VERSIONS = "keepflow.versions";  // { [noteId]: [{ at, title, content, checklist_items }, …] }
const VERSIONS_PER_NOTE = 10;

export function snapshotVersion(id, state) {
  const all = read(K_VERSIONS, {});
  const list = all[id] || [];
  const snap = {
    at: nowIso(),
    title: state.title || "",
    content: state.content || "",
    is_checklist: !!state.is_checklist,
    checklist_items: state.checklist_items || [],
    color: state.color || "default",
    images: state.images || [],
    attachments: state.attachments || [],
  };
  // Skip no-op snapshots to avoid filling the buffer with duplicates.
  const last = list[0];
  if (last && last.title === snap.title && last.content === snap.content
      && last.is_checklist === snap.is_checklist
      && JSON.stringify(last.checklist_items) === JSON.stringify(snap.checklist_items)
      && JSON.stringify(last.images) === JSON.stringify(snap.images)
      && JSON.stringify(last.attachments) === JSON.stringify(snap.attachments)) {
    return;
  }
  all[id] = [snap, ...list].slice(0, VERSIONS_PER_NOTE);
  write(K_VERSIONS, all);
}

export async function listNoteVersions(id) {
  const local = (read(K_VERSIONS, {})[id] || []).map((v) => ({ ...v, source: "local" }));
  if (!isSignedIn()) return local;
  try {
    const { data } = await tryRemote(() => client.get(`/notes/${id}/versions`));
    // Server versions win; fall back to local if the server has none yet.
    const normalized = (data || []).map((v) => ({
      at: v.snapshot_at || v.at,
      title: v.title || "",
      content: v.content || "",
      is_checklist: !!v.is_checklist,
      checklist_items: v.checklist_items || [],
      color: v.color || "default",
      images: v.images || [],
      attachments: v.attachments || [],
      source: "remote",
    }));
    return normalized.length ? normalized : local;
  } catch {
    return local;
  }
}


export async function deleteNote(id) {
  const after = read(K_NOTES, []).filter((n) => n.id !== id);
  write(K_NOTES, after);
  emit();
  if (!isSignedIn()) return;
  try { await tryRemote(() => client.delete(`/notes/${id}`)); }
  catch { enqueue({ type: "delete", id }); }
}

export async function emptyTrash() {
  const after = read(K_NOTES, []).filter((n) => !n.trashed);
  write(K_NOTES, after);
  emit();
  if (!isSignedIn()) return;
  try { await tryRemote(() => client.post("/notes/empty-trash")); }
  catch { enqueue({ type: "emptyTrash" }); }
}

// ---------------- Labels ----------------
export async function listLabels() {
  if (!isSignedIn()) {
    return read(K_LABELS, []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }
  if (isOnline()) {
    try {
      const { data } = await client.get("/labels");
      write(K_LABELS, data);
      emit();
    } catch (err) {
      // Offline/401 is expected — fall back to the local cache silently
      // but log transient 5xx so ops can see sync pressure.
      if (err?.response?.status >= 500) {
        console.warn("[offlineStore] /labels sync 5xx:", err.response.status);
      }
    }
  }
  return read(K_LABELS, []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
}

export async function createLabel(name) {
  const trimmed = (name || "").trim();
  if (!trimmed) return null;
  const existing = read(K_LABELS, []);
  if (existing.some((l) => l.name === trimmed)) return existing.find((l) => l.name === trimmed);
  const label = { id: genId(), name: trimmed, created_at: nowIso(), _localOnly: true };
  write(K_LABELS, [...existing, label]);
  emit();
  if (!isSignedIn()) return label;
  try {
    const { data } = await tryRemote(() => client.post("/labels", { name: trimmed }));
    const after = read(K_LABELS, []).map((l) => (l.id === label.id ? data : l));
    write(K_LABELS, after);
    emit();
    return data;
  } catch {
    enqueue({ type: "label.create", localId: label.id, name: trimmed });
    return label;
  }
}

export async function updateLabel(id, name) {
  const trimmed = (name || "").trim();
  if (!trimmed) return null;
  const labels = read(K_LABELS, []);
  const old = labels.find((l) => l.id === id);
  const oldName = old?.name;
  const after = labels.map((l) => (l.id === id ? { ...l, name: trimmed } : l));
  write(K_LABELS, after);
  if (oldName && oldName !== trimmed) {
    const notes = read(K_NOTES, []).map((n) =>
      (n.labels || []).includes(oldName)
        ? { ...n, labels: n.labels.map((x) => (x === oldName ? trimmed : x)), updated_at: nowIso() }
        : n
    );
    write(K_NOTES, notes);
  }
  emit();
  if (!isSignedIn()) return;
  try { await tryRemote(() => client.put(`/labels/${id}`, { name: trimmed })); }
  catch { enqueue({ type: "label.update", id, name: trimmed }); }
}

export async function setLabelColor(id, color) {
  const labels = read(K_LABELS, []);
  write(K_LABELS, labels.map((l) => (l.id === id ? { ...l, color } : l)));
  emit();
  // A label still waiting to be created on the server carries its colour with it.
  if (!isSignedIn() || labels.find((l) => l.id === id)?._localOnly) return;
  try { await tryRemote(() => client.put(`/labels/${id}`, { color })); }
  catch { enqueue({ type: "label.color", id, color }); }
}

export async function deleteLabel(id) {
  const labels = read(K_LABELS, []);
  const name = labels.find((l) => l.id === id)?.name;
  write(K_LABELS, labels.filter((l) => l.id !== id));
  if (name) {
    const notes = read(K_NOTES, []).map((n) =>
      (n.labels || []).includes(name)
        ? { ...n, labels: n.labels.filter((x) => x !== name), updated_at: nowIso() }
        : n
    );
    write(K_NOTES, notes);
  }
  emit();
  if (!isSignedIn()) return;
  try { await tryRemote(() => client.delete(`/labels/${id}`)); }
  catch { enqueue({ type: "label.delete", id }); }
}

// ---------------- Guest → User migration ----------------
// When a user signs in for the first time, we optionally offload any notes
// they created while in guest mode up to their fresh account. Called from
// AuthContext after /auth/callback succeeds.
export async function migrateGuestData() {
  if (!isSignedIn()) return { uploaded: 0 };
  const notes = read(K_NOTES, []);
  const localOnly = notes.filter((n) => n._localOnly);
  let uploaded = 0;
  for (const n of localOnly) {
    try {
      const { data } = await client.post("/notes", {
        title: n.title,
        content: n.content,
        is_checklist: n.is_checklist,
        checklist_items: n.checklist_items,
        color: n.color,
        labels: n.labels,
        pinned: n.pinned,
        reminder: n.reminder,
        images: n.images,
        attachments: n.attachments,
      });
      // Swap the local-only id with the server id.
      const after = read(K_NOTES, []).map((x) => (x.id === n.id ? { ...data, _localOnly: false } : x));
      write(K_NOTES, after);
      uploaded += 1;
    } catch (err) {
      console.warn("[migrate] note upload failed:", n.id, err?.response?.status, err?.message);
    }
  }
  // Also push any locally-created labels.
  const labels = read(K_LABELS, []).filter((l) => l._localOnly);
  for (const l of labels) {
    try {
      const { data } = await client.post("/labels", { name: l.name, color: l.color || null });
      const after = read(K_LABELS, []).map((x) => (x.id === l.id ? data : x));
      write(K_LABELS, after);
    } catch (err) {
      console.warn("[migrate] label upload failed:", l.name, err?.response?.status, err?.message);
    }
  }
  emit();
  return { uploaded };
}

// ---------------- Sync drain ----------------
export async function drainQueue() {
  if (!isOnline()) return { drained: 0, remaining: getQueue().length };
  const q = getQueue();
  const remaining = [];
  let drained = 0;
  for (const op of q) {
    try {
      if (op.type === "create") {
        const { data } = await client.post("/notes", op.payload);
        const after = read(K_NOTES, []).map((n) =>
          n.id === op.localId ? { ...data, _localOnly: false } : n
        );
        write(K_NOTES, after);
      } else if (op.type === "update") {
        await client.put(`/notes/${op.id}`, op.patch);
      } else if (op.type === "delete") {
        await client.delete(`/notes/${op.id}`);
      } else if (op.type === "emptyTrash") {
        await client.post("/notes/empty-trash");
      } else if (op.type === "label.create") {
        const color = read(K_LABELS, []).find((l) => l.id === op.localId)?.color || null;
        const { data } = await client.post("/labels", { name: op.name, color });
        const after = read(K_LABELS, []).map((l) =>
          l.id === op.localId ? data : l
        );
        write(K_LABELS, after);
      } else if (op.type === "label.update") {
        await client.put(`/labels/${op.id}`, { name: op.name });
      } else if (op.type === "label.color") {
        await client.put(`/labels/${op.id}`, { color: op.color });
      } else if (op.type === "label.delete") {
        await client.delete(`/labels/${op.id}`);
      }
      drained += 1;
    } catch {
      remaining.push(op);
    }
  }
  setQueue(remaining);
  emit();
  return { drained, remaining: remaining.length };
}

export const pendingOpsCount = () => getQueue().length;
export const lastSyncIso = () => read(K_LAST_SYNC, null);
